import React, { useEffect, useState, useCallback } from 'react';
import {
  ComposedChart, LineChart, Line, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import toast from 'react-hot-toast';
import api from '../api';

// Chart palette — validated for CVD + contrast in both themes.
// Red is the reserved status color (errors only), never a plain series.
const C = { primary: '#6366f1', secondary: '#0e9db2', danger: '#ef4444' };
const tooltipStyle = { background: '#1e293b', border: '1px solid #334155', borderRadius: 8 };
const tick = { fill: '#94a3b8', fontSize: 11 };

const fmtTime = ts => new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const fmtDateTime = ts => new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

function fmtUptime(sec) {
  if (sec == null) return '—';
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  return `${m}m ${sec % 60}s`;
}

function fmtDuration(ms) {
  if (!ms) return '—';
  const s = Math.round(ms / 1000);
  return fmtUptime(s);
}

const METRIC_RANGES = [{ label: '1h', hours: 1 }, { label: '6h', hours: 6 }, { label: '24h', hours: 24 }, { label: '7d', hours: 168 }];
const SOURCES = ['', 'express', 'process', 'console', 'client'];

export default function AdminMonitoring() {
  const [overview, setOverview] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [uptime, setUptime] = useState(null);
  const [errors, setErrors] = useState(null);
  const [hours, setHours] = useState(6);
  const [source, setSource] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [ov, mt, up, er] = await Promise.all([
        api.get('/monitoring/overview'),
        api.get('/monitoring/metrics', { params: { hours } }),
        api.get('/monitoring/uptime', { params: { days: 7 } }),
        api.get('/monitoring/errors', { params: { limit: 100, source: source || undefined } }),
      ]);
      setOverview(ov.data);
      setMetrics(mt.data.metrics);
      setUptime(up.data);
      setErrors(er.data);
    } catch {
      // keep the last good data on a failed poll
    } finally {
      setLoading(false);
    }
  }, [hours, source]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  const clearErrors = async () => {
    if (!window.confirm('Clear the entire error log?')) return;
    try {
      const r = await api.delete('/monitoring/errors');
      toast.success(`Cleared ${r.data.deleted} log entries`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to clear errors');
    }
  };

  if (loading) {
    return <div style={{ color: 'var(--text-muted)', padding: 40, textAlign: 'center' }}>Loading system monitor…</div>;
  }
  if (!overview) {
    return <div style={{ color: 'var(--text-muted)', padding: 40, textAlign: 'center' }}>Could not load monitoring data.</div>;
  }

  const lastHour = overview.last_hour || {};
  const healthy = overview.status === 'ok' && lastHour.error_rate < 5;
  const memPct = overview.sys_total_mb ? Math.round(((overview.sys_total_mb - overview.sys_free_mb) / overview.sys_total_mb) * 100) : null;
  const liveSeries = (lastHour.series || []).map(b => ({ ...b, time: fmtTime(b.ts) }));
  const metricSeries = metrics.map(m => ({ ...m, time: hours > 24 ? fmtDateTime(m.ts) : fmtTime(m.ts) }));

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">System Monitor</h1>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer' }}>
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
            Auto-refresh (15s)
          </label>
          <button className="btn btn-sm btn-ghost" onClick={load}>↻ Refresh</button>
        </div>
      </div>

      {/* Health stat cards */}
      <div className="stat-grid" style={{ marginBottom: 24 }}>
        <StatCard label="Status" color={healthy ? 'var(--success)' : 'var(--danger)'}
          value={healthy ? '● Healthy' : overview.db.connected ? '● Degraded' : '● DB Down'} />
        <StatCard label="Uptime" value={fmtUptime(overview.uptime_sec)} />
        <StatCard label={`Availability (7d)${uptime?.down_ms ? ` · ${fmtDuration(uptime.down_ms)} down` : ''}`}
          value={uptime ? `${uptime.availability}%` : '—'}
          color={uptime && uptime.availability < 99 ? 'var(--warning)' : 'var(--success)'} />
        <StatCard label="DB Ping" value={overview.db.ping_ms != null ? `${overview.db.ping_ms} ms` : overview.db.state}
          color={overview.db.connected ? undefined : 'var(--danger)'} />
        <StatCard label="Requests (1h)" value={(lastHour.requests || 0).toLocaleString()} />
        <StatCard label="Avg Response (1h)" value={`${lastHour.avg_ms || 0} ms`}
          color={lastHour.avg_ms > 1000 ? 'var(--warning)' : undefined} />
        <StatCard label="Error Rate (1h)" value={`${lastHour.error_rate || 0}%`}
          color={lastHour.error_rate > 5 ? 'var(--danger)' : lastHour.error_rate > 0 ? 'var(--warning)' : 'var(--success)'} />
        <StatCard label="Server Errors (24h)" value={(overview.errors_24h || 0).toLocaleString()}
          color={overview.errors_24h > 0 ? 'var(--warning)' : undefined} />
        <StatCard label="Client Errors (24h)" value={(overview.client_errors_24h || 0).toLocaleString()}
          color={overview.client_errors_24h > 0 ? 'var(--warning)' : undefined} />
        <StatCard label="Heap / RSS" value={`${overview.heap_used_mb} / ${overview.rss_mb} MB`} />
        <StatCard label={`System Memory${memPct != null ? ` · ${memPct}% used` : ''}`}
          value={`${((overview.sys_total_mb - overview.sys_free_mb) / 1024).toFixed(1)} / ${(overview.sys_total_mb / 1024).toFixed(1)} GB`}
          color={memPct > 90 ? 'var(--danger)' : memPct > 75 ? 'var(--warning)' : undefined} />
        <StatCard label="Event Loop Lag" value={`${overview.loop_lag_ms} ms`}
          color={overview.loop_lag_ms > 100 ? 'var(--warning)' : undefined} />
      </div>

      {/* Live traffic (in-memory, per minute) */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">Live traffic — requests per minute (last hour)</div>
        {liveSeries.every(b => !b.requests) ? (
          <div style={{ color: 'var(--text-muted)', padding: 30, textAlign: 'center' }}>No API traffic in the last hour</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={liveSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="time" tick={tick} minTickGap={40} />
              <YAxis tick={tick} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend formatter={v => <span style={{ color: '#94a3b8', fontSize: 12 }}>{v}</span>} />
              <Bar dataKey="requests" name="Requests" fill={C.primary} radius={[3, 3, 0, 0]} />
              <Line dataKey="errors" name="5xx Errors" stroke={C.danger} strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Historical metrics */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontWeight: 600 }}>Server metrics</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {METRIC_RANGES.map(r => (
            <button key={r.hours}
              className={`btn btn-sm ${hours === r.hours ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setHours(r.hours)}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {metricSeries.length === 0 ? (
        <div className="card" style={{ marginBottom: 20, color: 'var(--text-muted)', padding: 30, textAlign: 'center' }}>
          No snapshots yet — metrics are recorded every minute while the server runs.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
          <ChartCard title="Response time (ms)">
            <LineChart data={metricSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="time" tick={tick} minTickGap={40} />
              <YAxis tick={tick} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend formatter={v => <span style={{ color: '#94a3b8', fontSize: 12 }}>{v}</span>} />
              <Line dataKey="avg_ms" name="Average" stroke={C.primary} strokeWidth={2} dot={false} />
              <Line dataKey="max_ms" name="Slowest" stroke={C.secondary} strokeWidth={2} dot={false} />
            </LineChart>
          </ChartCard>

          <ChartCard title="Requests & errors per minute">
            <ComposedChart data={metricSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="time" tick={tick} minTickGap={40} />
              <YAxis tick={tick} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend formatter={v => <span style={{ color: '#94a3b8', fontSize: 12 }}>{v}</span>} />
              <Bar dataKey="requests" name="Requests" fill={C.primary} radius={[3, 3, 0, 0]} />
              <Line dataKey="errors_5xx" name="5xx Errors" stroke={C.danger} strokeWidth={2} dot={false} />
            </ComposedChart>
          </ChartCard>

          <ChartCard title="Process memory (MB)">
            <LineChart data={metricSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="time" tick={tick} minTickGap={40} />
              <YAxis tick={tick} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend formatter={v => <span style={{ color: '#94a3b8', fontSize: 12 }}>{v}</span>} />
              <Line dataKey="heap_used_mb" name="Heap used" stroke={C.primary} strokeWidth={2} dot={false} />
              <Line dataKey="rss_mb" name="RSS" stroke={C.secondary} strokeWidth={2} dot={false} />
            </LineChart>
          </ChartCard>

          <ChartCard title="CPU (%)">
            <LineChart data={metricSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="time" tick={tick} minTickGap={40} />
              <YAxis tick={tick} domain={[0, 100]} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line dataKey="cpu_percent" name="CPU" stroke={C.secondary} strokeWidth={2} dot={false} />
            </LineChart>
          </ChartCard>
        </div>
      )}

      {/* Downtime events */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">Downtime & restarts (last 7 days)</div>
        {!uptime || uptime.events.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', padding: 20, textAlign: 'center' }}>No lifecycle events recorded yet</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Event</th><th>When</th><th>Duration</th><th>Details</th></tr>
              </thead>
              <tbody>
                {uptime.events.map(e => (
                  <tr key={e._id}>
                    <td>
                      <span className={`badge ${e.type === 'downtime' ? 'badge-red' : 'badge-green'}`}>
                        {e.type === 'downtime' ? '⛔ Downtime' : '▲ Startup'}
                      </span>
                    </td>
                    <td>{fmtDateTime(e.ts)}</td>
                    <td>{e.type === 'downtime' ? fmtDuration(e.duration_ms) : '—'}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                      {e.type === 'downtime'
                        ? `Offline until ${fmtDateTime(e.ended_at)}`
                        : e.meta ? `pid ${e.meta.pid} · node ${e.meta.node}` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 12, marginBottom: 0 }}>
          Downtime is detected from gaps in the server's minute heartbeat, so it's recorded when the server comes back up.
        </p>
      </div>

      {/* Error log */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>
            Error log {errors ? `(${errors.total.toLocaleString()} total)` : ''}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {SOURCES.map(s => (
              <button key={s || 'all'}
                className={`btn btn-sm ${source === s ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setSource(s)}>
                {s || 'all'}
              </button>
            ))}
            <button className="btn btn-sm btn-ghost" style={{ color: 'var(--danger)' }} onClick={clearErrors}>
              Clear log
            </button>
          </div>
        </div>
        {!errors || errors.errors.length === 0 ? (
          <div style={{ color: 'var(--success)', padding: 30, textAlign: 'center' }}>✓ No errors detected</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Time</th><th>Source</th><th>Message</th><th>Where</th><th>User</th></tr>
              </thead>
              <tbody>
                {errors.errors.map(e => <ErrorRow key={e._id} e={e} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ErrorRow({ e }) {
  const [open, setOpen] = useState(false);
  const badge = { express: 'badge-red', process: 'badge-red', console: 'badge-yellow', client: 'badge-blue' }[e.source] || 'badge-red';
  return (
    <>
      <tr onClick={() => e.stack && setOpen(o => !o)} style={{ cursor: e.stack ? 'pointer' : 'default' }}>
        <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDateTime(e.createdAt)}</td>
        <td><span className={`badge ${badge}`}>{e.source}</span></td>
        <td style={{ maxWidth: 420 }}>
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: open ? 'normal' : 'nowrap' }}>
            {e.stack && <span style={{ color: 'var(--text-muted)', marginRight: 6 }}>{open ? '▾' : '▸'}</span>}
            {e.message}
          </div>
        </td>
        <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {e.route ? `${e.method || ''} ${e.route}${e.status ? ` → ${e.status}` : ''}` : e.meta?.url || '—'}
        </td>
        <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{e.user?.name || '—'}</td>
      </tr>
      {open && e.stack && (
        <tr>
          <td colSpan={5}>
            <pre style={{
              margin: 0, padding: 12, fontSize: 11, lineHeight: 1.5, overflowX: 'auto',
              background: 'var(--bg, rgba(0,0,0,0.15))', borderRadius: 8, color: 'var(--text-muted)',
            }}>{e.stack}</pre>
          </td>
        </tr>
      )}
    </>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="card">
      <div className="card-title">{title}</div>
      <ResponsiveContainer width="100%" height={220}>{children}</ResponsiveContainer>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="card" style={{ padding: '14px 18px' }}>
      <div style={{ fontSize: 20, fontWeight: 700, color: color || 'var(--text)', whiteSpace: 'nowrap' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{label}</div>
    </div>
  );
}
